import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Boxes, CircleDollarSign, Cpu, Gauge } from "lucide-react";

import { api } from "@/api/client";
import { useEdgeStats } from "@/api/local/runtime";
import type {
  CostTimeseriesResult,
  Pool,
  Replica,
  ReplicasDesired,
  Service,
} from "@/api/types";
import { HealthBar } from "@/components/HealthBar";
import { MetricTile, type Tone } from "@/components/MetricTile";
import { Sparkline } from "@/components/Sparkline";

interface Props {
  service: Service;
  pools: Pool[] | undefined;
  replicas: Replica[];
}

/**
 * Like the Global dashboard, the Service dashboard is now a graphical
 * summary only. Per-pool cost and recent events have moved to their own
 * dedicated tabs (Cost, Events) — duplicating them here was noise.
 */
export function ServiceDashboard({ service, pools, replicas }: Props) {
  const desired = useQuery({
    queryKey: ["service", service.id, "desired"],
    queryFn: () =>
      api.get<ReplicasDesired[]>(`/services/${service.id}/replicas-desired`),
  });
  const ts = useQuery({
    queryKey: ["cost-ts", "service", service.id],
    queryFn: () =>
      api.get<CostTimeseriesResult>(
        `/cost/timeseries?service_id=${service.id}&buckets=24&bucket_seconds=3600`,
      ),
  });
  const edge = useEdgeStats();

  const stats = useMemo(() => {
    const ready = replicas.filter((r) => r.status === "ready").length;
    const provisioning = replicas.filter(
      (r) => r.status === "pending" || r.status === "provisioning",
    ).length;
    const failed = replicas.filter(
      (r) => r.status === "failed" || r.status === "not_ready",
    ).length;
    const shutting = replicas.filter(
      (r) => r.status === "shutting_down",
    ).length;
    return {
      ready,
      provisioning,
      failed,
      shutting,
      total: replicas.length,
      // "Live" excludes `shutting_down` rows: SkyServe is already
      // tearing them down so they no longer hold a slot against the
      // cap. Drain stages still count because they continue to serve
      // traffic and consume capacity.
      live: replicas.length - shutting,
    };
  }, [replicas]);

  // Per-pool desired now; sum across pools for the service-level
  // aggregate (autoscaler / schedule pins / manual pins all land in
  // `replicas_desired`).
  const desiredTotal = (desired.data ?? []).reduce(
    (acc, d) => acc + d.desired_count,
    0,
  );
  // Cap = sum of every pool's `max_replicas` (the same denominator the
  // tree row uses). Reads `live/cap` so a ROLLING update overshoot
  // (old `draining*` + new ready/provisioning) still shows up as e.g.
  // `4/2`, while replicas already past the point of consuming capacity
  // (`shutting_down`) don't trip a bogus over-cap warning during a
  // routine drain.
  const capTotal = (pools ?? []).reduce(
    (acc, p) => acc + (p.max_replicas ?? 0),
    0,
  );
  const overCap = capTotal > 0 && stats.live > capTotal;
  const baseTone: Tone =
    stats.failed > 0
      ? "danger"
      : stats.total === 0
        ? "neutral"
        : stats.ready === stats.total
          ? "ok"
          : "warn";
  const replicaTone: Tone =
    overCap && baseTone !== "danger" ? "warn" : baseTone;

  return (
    <>
    <div className="metric-grid">
      <MetricTile
        icon={<Cpu />}
        label="Replicas"
        value={
          capTotal > 0
            ? `${stats.live}/${capTotal}`
            : `${stats.ready}/${stats.live}`
        }
        unit={
          overCap
            ? `+${stats.live - capTotal} over cap`
            : stats.shutting > 0
              ? `${stats.shutting} shutting down`
              : capTotal > 0
                ? `${stats.ready} ready`
                : `desired ${desiredTotal}`
        }
        tone={replicaTone}
        hint={
          <HealthBar
            ready={stats.ready}
            pending={stats.provisioning}
            failed={stats.failed}
            total={stats.live || capTotal || desiredTotal}
            width={140}
          />
        }
        rows={[
          { label: "ready", value: stats.ready },
          { label: "prov.", value: stats.provisioning },
          { label: "failed", value: stats.failed },
          { label: "desired", value: desiredTotal },
        ]}
      />
      <MetricTile
        icon={<Boxes />}
        label="Pools"
        value={pools?.length ?? 0}
        unit="configured"
        hint={
          pools?.length
            ? `priorities ${[...new Set(pools.map((p) => p.priority))]
                .sort((a, b) => a - b)
                .map((n) => "P" + n)
                .join(", ")}`
            : "no pools"
        }
      />
      <MetricTile
        icon={<CircleDollarSign />}
        label="Cost · 24h"
        value={`$${(ts.data?.total_usd ?? 0).toFixed(2)}`}
        tone={(ts.data?.total_usd ?? 0) > 0 ? "brand" : "neutral"}
        hint={
          <Sparkline
            values={(ts.data?.points ?? []).map((p) => p.cost_usd)}
            width={140}
            height={28}
            tone="brand"
            fill
          />
        }
      />
      <MetricTile
        icon={<Gauge />}
        label="Live req/sec"
        value={(edge.rps * 0.5).toFixed(1)}
        unit="rps · share"
        hint={
          <Sparkline
            values={edge.rps_history.map((v) => v * 0.5)}
            width={140}
            height={28}
            tone="brand"
            fill
          />
        }
      />
    </div>
    </>
  );
}
